import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { computeCourseProgress } from '@erp/domain/formations';
import type { ProFormationChapter } from '@erp/data-access/interfaces';

export const PRO_COURSE_KEY = ['pro-formation-course'] as const;

/**
 * Le parcours de prise en main de l'électricien connecté.
 *
 * Passe par `ds.formationPages.getProCourse()` → RPC `pro_formation_course()`,
 * et NON par une lecture de `formation_videos`. Les policies RLS s'additionnent :
 * un compte à la fois membre d'un tenant (tableau de bord) et pro d'un autre
 * verrait sinon remonter les formations INTERNES de son propre tenant dans son
 * parcours électricien. Même raisonnement que `useProCatalog`.
 *
 * Volontairement hors du cache persisté (voir `PERSISTED_QUERY_ROOTS`) : la
 * progression est une donnée par utilisateur, elle ne va pas sur le disque.
 */
export function useProCourse() {
  const ds = useDataService();
  return useQuery({
    queryKey: PRO_COURSE_KEY,
    queryFn: () => ds.formationPages.getProCourse(),
    staleTime: 5 * 60 * 1000,
  });
}

/** La progression dérivée du parcours — sans refetch quand il est en cache. */
export function useProCourseProgress() {
  const query = useProCourse();
  const chapters = query.data ?? [];
  return {
    ...computeCourseProgress(chapters),
    chapters,
    isLoading: query.isLoading,
    /** Vrai quand le tenant n'a tout simplement pas publié de parcours. */
    hasCourse: chapters.some((c) => c.lessons.length > 0),
  };
}

function patchLesson(
  chapters: ProFormationChapter[],
  lessonId: string,
  completedAt: string | null,
): ProFormationChapter[] {
  return chapters.map((chapter) => ({
    ...chapter,
    lessons: chapter.lessons.map((lesson) =>
      lesson.id === lessonId ? { ...lesson, completedAt } : lesson,
    ),
  }));
}

/** Cocher / décocher une leçon. Optimiste : la coche répond au clic. */
export function useSetLessonProgress() {
  const ds = useDataService();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ lessonId, completed }: { lessonId: string; completed: boolean }) =>
      ds.formationPages.setProLessonProgress(lessonId, completed),
    onMutate: async ({ lessonId, completed }) => {
      await qc.cancelQueries({ queryKey: PRO_COURSE_KEY });
      const previous = qc.getQueryData<ProFormationChapter[]>(PRO_COURSE_KEY);
      qc.setQueryData<ProFormationChapter[]>(PRO_COURSE_KEY, (old) =>
        old ? patchLesson(old, lessonId, completed ? new Date().toISOString() : null) : old,
      );
      return { previous };
    },
    onError: (_e, _vars, context) => {
      if (context?.previous) qc.setQueryData(PRO_COURSE_KEY, context.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: PRO_COURSE_KEY });
    },
  });
}

/** « Je connais déjà » — marque tout le parcours comme terminé d'un coup. */
export function useCompleteAllLessons() {
  const ds = useDataService();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () => ds.formationPages.completeAllProLessons(),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: PRO_COURSE_KEY });
      const previous = qc.getQueryData<ProFormationChapter[]>(PRO_COURSE_KEY);
      const now = new Date().toISOString();
      qc.setQueryData<ProFormationChapter[]>(PRO_COURSE_KEY, (old) =>
        old?.map((chapter) => ({
          ...chapter,
          lessons: chapter.lessons.map((lesson) => ({
            ...lesson,
            completedAt: lesson.completedAt ?? now,
          })),
        })),
      );
      return { previous };
    },
    onError: (_e, _vars, context) => {
      if (context?.previous) qc.setQueryData(PRO_COURSE_KEY, context.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: PRO_COURSE_KEY });
    },
  });
}

/** Recommencer le parcours depuis zéro. */
export function useResetCourseProgress() {
  const ds = useDataService();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () => ds.formationPages.resetProProgress(),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: PRO_COURSE_KEY });
      const previous = qc.getQueryData<ProFormationChapter[]>(PRO_COURSE_KEY);
      qc.setQueryData<ProFormationChapter[]>(PRO_COURSE_KEY, (old) =>
        old?.map((chapter) => ({
          ...chapter,
          lessons: chapter.lessons.map((lesson) => ({ ...lesson, completedAt: null })),
        })),
      );
      return { previous };
    },
    onError: (_e, _vars, context) => {
      if (context?.previous) qc.setQueryData(PRO_COURSE_KEY, context.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: PRO_COURSE_KEY });
    },
  });
}
